import React, { useState, useCallback, memo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Collapse,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import axios from 'axios';

interface ResultItem {
  id: string;
  title: string;
  main_body: string;
  official_paragraph_number: string;
  composite_text_original?: string;
  provision_name: string;
  provision_abbrev: string;
  provision_version: string;
  provision_entity_id: string;
  version_id: string;
  navigationUrl?: string;
  vector_score: number;
  keyword_score: number;
  combined_score: number;
}

interface ProvisionGroup {
  provision_name: string;
  provision_type: string;
  provision_abbrev: string;
  itemCount: number;
  expanded: boolean;
  items: ResultItem[];
}

// Memoized ResultItem component
const ResultItemComponent = memo<{
  result: ResultItem;
  rIdx: number;
  getScoreColor: (s: number) => string;
}>(({ result, rIdx, getScoreColor }) => (
  <Box
    sx={{
      p: 2,
      mb: 1,
      border: '1px solid #e0e0e0',
      borderRadius: 1,
      backgroundColor: '#fafafa',
      '&:hover': { backgroundColor: '#f5f5f5' },
    }}
  >
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
          [{rIdx + 1}]
        </Typography>
        {result.navigationUrl ? (
          <Typography
            component="a"
            href={result.navigationUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="subtitle2"
            sx={{
              fontWeight: 'bold',
              color: '#1976d2',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
                color: '#1565c0',
              },
              cursor: 'pointer',
              flex: 1,
            }}
          >
            {result.title}
          </Typography>
        ) : (
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', flex: 1 }}>
            {result.title}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color="textSecondary">
        Section {result.official_paragraph_number} • v{result.provision_version}
      </Typography>
    </Box>

    <Typography
      variant="body2"
      sx={{
        mb: 1.5,
        lineHeight: 1.5,
        fontStyle: 'italic',
        color: '#555',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}
    >
      &quot;{result.main_body}&quot;
    </Typography>

    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
      <Box>
        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
          Vector Score
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: '100%',
              height: 6,
              backgroundColor: '#e0e0e0',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${result.vector_score * 100}%`,
                height: '100%',
                backgroundColor: getScoreColor(result.vector_score),
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ minWidth: '30px', fontWeight: 'bold', textAlign: 'right' }}>
            {result.vector_score.toFixed(3)}
          </Typography>
        </Box>
      </Box>

      <Box>
        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
          Keyword Score
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: '100%',
              height: 6,
              backgroundColor: '#e0e0e0',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${result.keyword_score * 100}%`,
                height: '100%',
                backgroundColor: getScoreColor(result.keyword_score),
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ minWidth: '30px', fontWeight: 'bold', textAlign: 'right' }}>
            {result.keyword_score.toFixed(3)}
          </Typography>
        </Box>
      </Box>

      <Box>
        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
          Combined Score
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: '100%',
              height: 6,
              backgroundColor: '#e0e0e0',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${result.combined_score * 100}%`,
                height: '100%',
                backgroundColor: getScoreColor(result.combined_score),
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ minWidth: '30px', fontWeight: 'bold', textAlign: 'right' }}>
            {result.combined_score.toFixed(3)}
          </Typography>
        </Box>
      </Box>
    </Box>
  </Box>
));
ResultItemComponent.displayName = 'ResultItem';

// Memoized ResultsList component
const ResultsListComponent = memo<{
  results: ProvisionGroup[];
  expandedGroups: Map<string, boolean>;
  onToggleProvision: (p: string) => void;
  getScoreColor: (s: number) => string;
}>(({ results, expandedGroups, onToggleProvision, getScoreColor }) => (
  <Grid container spacing={2}>
    {results.map((group, groupIndex) => (
      <Grid item xs={12} key={group.provision_name}>
        <Card sx={{ position: 'relative' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid #e0e0e0',
              cursor: 'pointer',
            }}
            onClick={() => onToggleProvision(group.provision_name)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
              >
                {groupIndex + 1}
              </Box>

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.3 }}>
                  {group.provision_name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {group.itemCount} result{group.itemCount !== 1 ? 's' : ''} • {group.provision_type}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                <Chip label={group.provision_abbrev} size="small" variant="outlined" />
              </Box>
            </Box>

            <Box
              sx={{
                ml: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                cursor: 'pointer',
                transition: 'transform 0.3s ease',
                transform: expandedGroups.get(group.provision_name)
                  ? 'rotate(180deg)'
                  : 'rotate(0deg)',
              }}
            >
              <Typography sx={{ fontSize: '18px', fontWeight: 'bold', color: '#666' }}>
                ▼
              </Typography>
            </Box>
          </Box>

          <Collapse in={expandedGroups.get(group.provision_name) ?? true} timeout="auto" unmountOnExit>
            <CardContent sx={{ pt: 2, pb: 2 }}>
              <Grid container spacing={2}>
                {group.items.map((result, itemIndex) => (
                  <Grid item xs={12} key={result.id}>
                    <ResultItemComponent
                      result={result}
                      rIdx={itemIndex}
                      getScoreColor={getScoreColor}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Collapse>
        </Card>
      </Grid>
    ))}
  </Grid>
));
ResultsListComponent.displayName = 'ResultsList';

export default function SemanticSearch() {
  const [query, setQuery] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [results, setResults] = useState<ProvisionGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Map<string, boolean>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  // Filter controls
  const [sliderDisplayValue, setSliderDisplayValue] = useState(0.3);
  const [keywordSearchStrategy, setKeywordSearchStrategy] = useState('PROPORTIONAL');

  const normalizeResults = (data: any, threshold: number = 0): ProvisionGroup[] => {
    const dataResults = data.results || [];

    if (!Array.isArray(dataResults) || dataResults.length === 0) {
      return [];
    }

    // Group by provision
    const groupMap = new Map<string, ProvisionGroup>();

    for (const result of dataResults) {
      const key = result.provision_name || 'Unknown';

      // Apply threshold filter
      const score = result.combined_score ?? 0;
      if (Number(score) < threshold) {
        continue;
      }

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          provision_name: result.provision_name || 'Unknown',
          provision_type: result.provision_type || 'Unknown',
          provision_abbrev: result.provision_abbrev || '',
          itemCount: 0,
          expanded: true,
          items: [],
        });
      }

      const group = groupMap.get(key)!;
      group.itemCount += 1;
      group.items.push({
        id: result.id,
        title: result.title || '(no title)',
        main_body: result.main_body || '',
        official_paragraph_number: result.official_paragraph_number || '',
        composite_text_original: result.composite_text_original,
        provision_name: result.provision_name,
        provision_abbrev: result.provision_abbrev,
        provision_version: result.provision_version,
        provision_entity_id: result.provision_entity_id,
        version_id: result.version_id,
        navigationUrl: result.navigationUrl,
        vector_score: result.vector_score || 0,
        keyword_score: result.keyword_score || 0,
        combined_score: result.combined_score || 0,
      });
    }

    // Sort items within each group by combined score
    return Array.from(groupMap.values()).map((group) => ({
      ...group,
      items: group.items.sort((a, b) => b.combined_score - a.combined_score),
    }));
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!query.trim()) {
      setError('Please enter a search query');
      return;
    }

    setLoading(true);
    setError(null);
    setSearched(true);
    setResults([]);
    setExpandedGroups(new Map());

    try {
      const headers = apiToken ? { Authorization: apiToken } : {};
      const response = await axios.post('/api/search/semantic', {
        text: query,
        limit: 100,
        vectorWeight: 0.6,
        keywordWeight: 0.4,
        minCombinedScore: sliderDisplayValue,
        keywordSearchStrategy: keywordSearchStrategy,
      }, { headers });

      const normalizedResults = normalizeResults(response.data, sliderDisplayValue);
      setResults(normalizedResults);

      // Initialize all groups as expanded
      const expanded = new Map<string, boolean>();
      normalizedResults.forEach((group) => {
        expanded.set(group.provision_name, true);
      });
      setExpandedGroups(expanded);

      if (normalizedResults.length === 0) {
        setError(`No results found with current filters. Try adjusting parameters.`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Search failed';
      setError(message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSliderChange = (newValue: number) => {
    setSliderDisplayValue(newValue);
  };

  const toggleGroupExpanded = useCallback((provision: string) => {
    setExpandedGroups((prev) => {
      const newMap = new Map(prev);
      newMap.set(provision, !newMap.get(provision));
      return newMap;
    });
  }, []);

  const getScoreColor = useCallback((score: number) => {
    if (score >= 0.8) return '#4caf50'; // green
    if (score >= 0.6) return '#ff9800'; // orange
    return '#f44336'; // red
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 2, fontWeight: 'bold' }}>
          Semantic Search
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
          Search legal provisions using natural language. Results are powered by AI semantic
          understanding.
        </Typography>

        {/* Search Form */}
        <Card component="form" onSubmit={handleSearch} sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search: e.g., 'GDPR data retention rules', 'vehicle safety requirements', etc."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={loading}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
              sx={{ px: 3 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Searching...
                </>
              ) : (
                'Search'
              )}
            </Button>
          </Box>

          {/* API Token Field */}
          <TextField
            fullWidth
            placeholder="Optional: Paste API token here (64-character hex string)"
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            disabled={loading}
            size="small"
            sx={{ mb: 2 }}
            helperText="Using an API token instead of JWT. Leave empty to use default authentication."
          />

          {/* Filter Controls Card */}
          <Card sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
            <Grid container spacing={2}>
              {/* Keyword Strategy Dropdown - 1/5 width */}
              <Grid item xs={12} sm={2.4}>
                <FormControl fullWidth>
                  <InputLabel>Keyword Strategy</InputLabel>
                  <Select
                    value={keywordSearchStrategy}
                    label="Keyword Strategy"
                    onChange={(e) => setKeywordSearchStrategy(e.target.value)}
                    disabled={loading}
                  >
                    <MenuItem value="OR">
                      <Box>
                        <Typography variant="body2">OR</Typography>
                        <Typography variant="caption" color="textSecondary">Any keyword</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="AND">
                      <Box>
                        <Typography variant="body2">AND</Typography>
                        <Typography variant="caption" color="textSecondary">All keywords</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="PROPORTIONAL">
                      <Box>
                        <Typography variant="body2">PROPORTIONAL</Typography>
                        <Typography variant="caption" color="textSecondary">Partial credit</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="AT_LEAST_HALF">
                      <Box>
                        <Typography variant="body2">AT_LEAST_HALF</Typography>
                        <Typography variant="caption" color="textSecondary">≥50%</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Result Quality Threshold Slider - 4/5 width */}
              <Grid item xs={12} sm={9.6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Result Quality Threshold: {sliderDisplayValue.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                    Lower = more results (higher recall), Higher = fewer but more relevant results (higher precision)
                  </Typography>
                  <Slider
                    value={sliderDisplayValue}
                    onChange={(e, value) => handleSliderChange(Array.isArray(value) ? value[0] : value)}
                    min={0}
                    max={1}
                    step={0.01}
                    marks={[
                      { value: 0, label: '0.0 (All)' },
                      { value: 0.3, label: '0.3 (Default)' },
                      { value: 1.0, label: '1.0 (Exact)' },
                    ]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => value.toFixed(2)}
                    disabled={loading}
                  />
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic', color: '#666' }}>
                    💡 Tip: Adjust the threshold, then click "Search" to apply the new filter
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Embeddings Management Info */}
        {results.length === 0 && !searched && (
          <Card sx={{ p: 3, mb: 3, backgroundColor: '#e3f2fd', border: '2px solid #1976d2' }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
              ℹ️ Embedding Management
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              To populate embeddings for legal provisions, please visit the{' '}
              <strong>Admin</strong> tab. There you can manage test provisions, individual provision
              indexing, and bulk operations.
            </Typography>
          </Card>
        )}

        {/* Results Count */}
        {searched && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {loading
                ? 'Searching...'
                : `Found ${results.length} provision group${results.length !== 1 ? 's' : ''} with ${results.reduce((acc, g) => acc + g.itemCount, 0)} total result${results.reduce((acc, g) => acc + g.itemCount, 0) !== 1 ? 's' : ''}`}
            </Typography>
          </Box>
        )}

        {/* Results List */}
        {results.length > 0 && (
          <ResultsListComponent
            results={results}
            expandedGroups={expandedGroups}
            onToggleProvision={toggleGroupExpanded}
            getScoreColor={getScoreColor}
          />
        )}

        {/* Empty State */}
        {searched && !loading && results.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary">
              No results found. Try different keywords or adjust filters.
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
}
